import { Card, FormControl, Grid, MenuItem, Modal, Select, SelectChangeEvent, Typography } from '@mui/material';
import CancelIcon from '../../../icons/close.svg';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { CompanyStaffDto, UpdateWorkOrderDto, VendorV2Dto, WorkOrderDto, WorkOrderStaffEmailDto, WorkOrderVendorEmailDto } from '@inosio/payments-sdk';
import { makeStyles } from '@mui/styles';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSendWorkOrderEmail, selectWorkOrderState } from '../sagas/workorderSlice';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  workOrderUuid: string;
  workOrderDto: WorkOrderDto;
}

type Assignee = {
  staff?: CompanyStaffDto;
  vendor?: VendorV2Dto;
  label: string;
  Uuid: string;
};

const SendWorkOrderEmailModal = (props: ModalProps) => {
  const { isSendingWorkOrderEmail, isSendingWorkOrderEmailSuccess } = useSelector(selectWorkOrderState);
  const dispatch = useDispatch();
  const [selectedAssignee, setSelectedAssignee] = useState<Assignee>();
  const { Vendors = [], Staff = [] } = props.workOrderDto;
  const classes = useStyles();
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    height: '523px',
    pointerEvents: 'auto',
    width: '472px',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleSendEmail = async () => {
    if (selectedAssignee) {
      const staff: WorkOrderStaffEmailDto | undefined = selectedAssignee.staff ? { Uuid: selectedAssignee.staff.Uuid } : undefined;
      const vendor: WorkOrderVendorEmailDto | undefined = selectedAssignee.vendor ? { Uuid: selectedAssignee.vendor.Uuid } : undefined;
      dispatch({
        type: getSendWorkOrderEmail.type,
        payload: {
          woUuid: props.workOrderUuid,
          updateWoDto: {
            SendEmail: {
              Vendor: vendor,
              Staff: staff,
            },
          } as UpdateWorkOrderDto,
        },
      });
    }
  };

  const assignees: Assignee[] = useMemo(() => {
    return [
      ...Staff.map((staff) => ({
        staff,
        label: [staff.FirstName, staff.LastName].filter(Boolean).join(' '),
        Uuid: staff.Uuid,
      })),
      ...Vendors.map((vendor) => ({
        vendor,
        label: vendor.Name || '',
        Uuid: vendor.Uuid,
      })),
    ];
  }, [Staff, Vendors]);

  useEffect(() => {
    if (assignees.length === 1) {
      setSelectedAssignee(assignees[0]);
    }
  }, [assignees]);

  const handleAssigneeChanged = (e: SelectChangeEvent<unknown>) => {
    const assignee = assignees.find((assignee) => assignee.Uuid === e.target.value);
    if (assignee) {
      setSelectedAssignee(assignee);
    }
  };

  useEffect(() => {
    if (isSendingWorkOrderEmailSuccess) {
      props.onClose();
    }
  }, [isSendingWorkOrderEmailSuccess]);

  return (
    <div>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown
        disableAutoFocus={true}
      >
        <Card sx={style}>
          <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans !important',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  fontSize: '18px !important',
                  lineHeight: '21px !important',
                  color: '#14142B',
                }}
              >
                Send Work Order
              </Typography>
            </Grid>

            <Grid item mt={4} rowSpacing={2} sx={{ width: '90%', height: '100%' }}>
              <Grid container sx={{ alignItems: 'center', my: 1 }}>
                {/* Label for Assignee */}
                <Grid item xs={3}>
                  <Typography sx={{ textAlign: 'right', marginRight: '10px' }}>Assignee:</Typography>
                </Grid>
                {/* Select dropdown for Assignee */}
                <Grid item xs={9}>
                  {assignees.length > 1 ? (
                    <FormControl sx={{ width: '100%' }} size="small">
                      <Select className={classes.AssigneeSelect} fullWidth value={selectedAssignee?.Uuid || ''} onChange={handleAssigneeChanged}>
                        {assignees.map((item) => (
                          <MenuItem key={item.Uuid} value={item.Uuid}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Grid item className={classes.inputBox}>
                      <Typography
                        sx={{
                          textAlign: 'right',
                        }}
                      >
                        {selectedAssignee?.label}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid container sx={{ alignItems: 'center', my: 1 }}>
                {/* Label for Email */}
                <Grid item xs={3}>
                  <Typography sx={{ textAlign: 'right', marginRight: '10px' }}>Email:</Typography>
                </Grid>

                {/* Email Box */}
                <Grid item xs={9}>
                  <Grid item className={classes.inputBox}>
                    <Typography
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {selectedAssignee?.staff?.Email || selectedAssignee?.vendor?.Email}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  variant="contained"
                  loading={isSendingWorkOrderEmail}
                  disabled={!selectedAssignee || isSendingWorkOrderEmail}
                  style={{
                    textTransform: 'none',
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 700,
                    backgroundColor: '#39A2DB',
                    borderRadius: '24px',
                  }}
                  onClick={handleSendEmail}
                >
                  Send
                </LoadingButton>
              </Box>
            </Box>

            {/* Close Icon */}
            <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
              <img
                src={CancelIcon}
                alt="close"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  handleClose();
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Modal>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  AssigneeSelect: {
    border: '2px solid #EEEEEE',
    textAlign: 'right',
    borderRadius: '24px !important',
    width: '100%',
    height: '90% !important',
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '16px !important',
    lightHeight: '22px !important',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #39A2DB !important',
    },
    '&.Mui-disabled .MuiSvgIcon-root ': {
      fill: '#F1F0F5 !important',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #F1F0F5 !important',
    },
    '& input.MuiInputBase-input::placeholder': {
      color: '#3CA2DC',
      textAlign: 'center',
      opacity: 1,
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'unset',
      margin: 0,
    },
  },
  inputBox: {
    width: '100%',
    border: '2px solid #EEEEEE',
    borderRadius: '24px',
    minHeight: '40px',
    height: 'auto',
    marginTop: '2px',
    marginBottom: '5px',
    padding: '7px 10px;',
  },
}));

export default SendWorkOrderEmailModal;
